import { z } from 'zod';
/**
 * Request contracts for the Basecamp porting queue (W8).
 *
 * The unit of work is a **(founder, Basecamp project) pair**, so these schemas
 * name a project everywhere a founder is named. Confirming three projects for a
 * founder enqueues three items, each retried and reported on its own.
 *
 * The interesting one is {@link basecampQueueProjectsSchema}. Everything a
 * founder brings across is decided by its two required fields:
 *
 * - `projects` — the explicit list. There is no `query`, no `suggestion_id` and
 *   no "use the best match" flag anywhere in this file, because a queue that
 *   accepts any of those has an automatic name-matching path whatever the
 *   comment above it says. Suggestions are a separate, read-only endpoint.
 * - `confirmed` — must be literally `true`. A boolean that can only hold one
 *   value looks redundant and is not: it is the difference between a client that
 *   meant to queue these projects and a client that posted a form it had not
 *   finished filling in. A Basecamp project is claimed by exactly one workspace
 *   forever, so a wrong queue entry is not undone by re-running.
 */
/** Basecamp ids are bigints; they travel as digit strings or safe integers. */
export const basecampProjectId = z
    .union([
    z.string().trim().regex(/^\d+$/, 'Basecamp project ids are numeric'),
    z.number().int().positive(),
])
    .transform((value) => String(value));
/** One confirmed project. The name is carried so the queue reads without Basecamp. */
export const basecampConfirmedProjectSchema = z.object({
    basecamp_project_id: basecampProjectId,
    /** As it read in Basecamp at confirmation time. Never authoritative. */
    basecamp_project_name: z.string().trim().max(500).optional(),
});
export const basecampQueueProjectsSchema = z.object({
    founder_user_id: z.string().uuid(),
    /**
     * Deduplicated by the queue, not here — an admin pasting the same project
     * twice is a slip, not an error worth a 400.
     */
    projects: z
        .array(basecampConfirmedProjectSchema)
        .min(1, 'Confirm at least one Basecamp project'),
    /** Explicit human confirmation. Nothing is queued from a name match. */
    confirmed: z.literal(true, {
        errorMap: () => ({
            message: 'confirmed must be true — projects have to be confirmed against Basecamp by a person, ' +
                'because a project is claimed by one workspace permanently.',
        }),
    }),
    /** Higher runs sooner. This is the pace-and-order knob. Applied to each item. */
    priority: z.number().int().min(-1000).max(1000).optional(),
});
export const basecampPortingPrioritySchema = z.object({
    priority: z.number().int().min(-1000).max(1000),
});
/**
 * Options for one run of one pair.
 *
 * `transfer_files: false` is the **structure-first** pass: collections, lists,
 * tasks and comments in seconds, without moving hundreds of megabytes. It is the
 * right first run while a project list is still being confirmed — an admin can
 * see what landed and tell whether they picked the right project *before* paying
 * for the blobs. Turning it on afterwards costs nothing: the import is
 * resumable, so the second pass moves exactly the files the first one skipped.
 *
 * A property of the attempt, not of the pair, which is why it lives in the run
 * request and not on the queue row.
 *
 * There is deliberately no field for reporting bytes. The import engine moves
 * the files and reports what it moved (`report.files.bytesTransferred`); the
 * queue records that. An endpoint for setting a byte total by hand would be a
 * way to walk a founder past the storage cap.
 */
export const basecampPortingRunSchema = z.object({
    transfer_files: z.boolean().optional(),
});
/**
 * Options for one export of one **project**.
 *
 * Note what is absent: a founder. An export is keyed on the project alone —
 * ownership is unknown when a verbatim copy is taken and is not needed to take
 * one — so the whole export campaign can run before anyone touches the
 * founder→project picker. Forcing a founder in here would invent an association
 * nobody has confirmed, which is exactly what the porting queue's confirm step
 * exists to prevent.
 *
 * `force` re-verifies against the bucket instead of short-circuiting on a
 * `complete: true` manifest; it still fetches nothing already stored.
 *
 * `transfer_files: false` takes the JSON without the blobs. The manifest still
 * counts the blobs it did not take and stays **incomplete**, so an import will
 * refuse it — which is the intended shape for "let me see what is in this project
 * before I pay for hundreds of megabytes", and deliberately not a shortcut to an
 * importable copy.
 */
export const basecampExportRunSchema = z.object({
    force: z.boolean().optional(),
    transfer_files: z.boolean().optional(),
    /**
     * Which project statuses to take. Defaults to the exporter's own default
     * (active only). Archived is deferred, not dropped — widening it here is what
     * "later" looks like.
     */
    project_statuses: z
        .array(z.enum(['active', 'archived', 'trashed']))
        .min(1)
        .optional(),
});
//# sourceMappingURL=basecamp-porting.js.map